import React, { FunctionComponent, useContext } from 'react';
import { keyBy } from 'lodash';
import { Intl } from '@erecruit/core';
import {
  FormItem,
  H1,
  Checkbox,
  FormContext,
  FormContextType,
} from '@erecruit/ui-kit';
import { MapWithMarker } from '@erecruit/ui-kit';
import { PositionTemplateFieldType, Resources } from '@erecruit/types';
import { fetchFacilityDepartments, LoadMoreSelect } from '@erecruit/modules';

import { Disclaimer } from '../Disclaimer';
import { generateFieldOptions } from '../../helpers';
import { PositionField } from '../../types';
import { FormFieldItem } from '../DynamicFormItems/Fields';
import { useWorksite } from '../DynamicFormItems/helpers';
import AsyncSelect from '../DynamicFormItems/AsyncSelect';
import { useAppConfig } from '../../../../appConfig';

const { FormattedMessage, injectIntl } = Intl;

const messages = messageIds.vms.newPositionWizard.location;

export interface LocationStepProps {
  fields: PositionField[];
  isEdit?: boolean;
}

export interface WorksiteLocationDetailsProps {
  worksiteLocation: Resources.Worksite;
}

export interface LocationStepForm {
  worksiteAddress: number;
  facilityDepartments: number[];
  workFromHome: boolean;
}

const LocationStep: FunctionComponent<LocationStepProps &
  Intl.InjectedIntlProps> = ({ intl, fields, isEdit }) => {
  const keyFields = keyBy(fields, 'fieldType');

  const form = useContext<FormContextType<LocationStepForm>>(FormContext);

  const { getFieldsValue } = form;
  const {
    workFromHome,
    facilityDepartments,
    worksiteAddress,
  } = getFieldsValue();

  const worksiteAddressField =
    keyFields[PositionTemplateFieldType.WorksiteAddress];
  const facilityDepartmentsField =
    keyFields[PositionTemplateFieldType.FacilityDepartments];
  const workFromHomeField = keyFields[PositionTemplateFieldType.WorkFromHome];

  const appConfig = useAppConfig();
  const { worksite } = useWorksite(worksiteAddressField, worksiteAddress);

  if (!worksiteAddressField) {
    return null;
  }

  return (
    <div>
      {!isEdit && (
        <>
          <H1>
            <FormattedMessage id={messages.title} />
          </H1>
          <Disclaimer>
            <FormattedMessage id={messages.disclaimer} />
          </Disclaimer>
        </>
      )}
      {worksiteAddressField && (
        <FormFieldItem
          field={worksiteAddressField}
          messageFormatId="vms.newPositionWizard"
        >
          <AsyncSelect field={worksiteAddressField} />
        </FormFieldItem>
      )}

      {worksite && worksite.latitude && worksite.longitude && (
        <MapWithMarker
          googleMapsKey={appConfig?.REACT_APP_GOOGLE_MAPS_KEY || ''}
          lat={worksite.latitude}
          lng={worksite.longitude}
        />
      )}

      {workFromHomeField && (
        <FormItem
          id="workFromHome"
          hasFeedback
          options={generateFieldOptions(workFromHomeField, workFromHome)}
        >
          <Checkbox
            checked={workFromHome}
            disabled={
              workFromHomeField.settings && workFromHomeField.settings.locked
            }
          >
            {intl.formatMessage({
              id: messages.workFromHome,
            })}
          </Checkbox>
        </FormItem>
      )}

      {facilityDepartmentsField && (
        <FormItem
          id="facilityDepartments"
          label={intl.formatMessage({
            id: messages.facilityDepartments,
          })}
          hasFeedback
          options={generateFieldOptions(
            facilityDepartmentsField,
            facilityDepartments,
          )}
        >
          <LoadMoreSelect
            fetchEntities={fetchFacilityDepartments}
          ></LoadMoreSelect>
        </FormItem>
      )}
    </div>
  );
};

export default injectIntl(LocationStep);
